import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { apiClient } from '../utils/apiClient';
import { SessionFormData, Talk, Event } from '../types';

const SessionFormPage: React.FC = () => {
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(window.location.search);
  const preselectedEventId = searchParams.get('event') || '';

  const [formData, setFormData] = useState<SessionFormData>({
    talk_id: '',
    event_id: preselectedEventId,
    start_time: '',
    room_or_track: ''
  });
  const [talks, setTalks] = useState<Talk[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [showNewEventForm, setShowNewEventForm] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setFetchLoading(true);
      const [talksResponse, eventsResponse] = await Promise.all([
        apiClient.get('/v1/talks'),
        apiClient.get('/v1/events', { params: { status: 'public' } })
      ]);
      setTalks(talksResponse.data.data?.talks || []);
      setEvents(eventsResponse.data.data?.events || []);
    } catch (err: any) {
      console.error('Error fetching data:', err);
      setError(err.response?.data?.error?.message || 'Failed to load data');
    } finally {
      setFetchLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.talk_id) {
      errors.talk_id = 'Please select a talk';
    }

    if (!formData.event_id && !showNewEventForm) {
      errors.event_id = 'Please select an event or create a new one';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const payload: any = {
        talk_id: formData.talk_id,
        event_id: formData.event_id || undefined,
        start_time: formData.start_time || undefined,
        room_or_track: formData.room_or_track || undefined
      };

      const response = await apiClient.post('/v1/sessions', payload);
      const newSessionId = response.data.data.session_id;
      navigate(`/sessions/${newSessionId}`);
    } catch (err: any) {
      console.error('Error creating session:', err);
      setError(err.response?.data?.error?.message || 'Failed to create session');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  if (fetchLoading) {
    return (
      <div className="container mt-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="row justify-content-center">
        <div className="col-lg-8">
          <div className="mb-3">
            <Link to="/sessions" className="btn btn-link text-decoration-none">
              <i className="fas fa-arrow-left me-2"></i>
              Back to Sessions
            </Link>
          </div>

          <div className="card">
            <div className="card-header bg-white">
              <h3 className="mb-0">
                <i className="fas fa-calendar-alt me-2"></i>
                Create New Session
              </h3>
            </div>
            <div className="card-body">
              {error && (
                <div className="alert alert-danger" role="alert">
                  <i className="fas fa-exclamation-circle me-2"></i>
                  {error}
                </div>
              )}

              {talks.length === 0 && (
                <div className="alert alert-warning" role="alert">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  You need to create a talk before you can create a session.
                  <Link to="/talks/new" className="btn btn-sm btn-primary ms-2">
                    Create Talk
                  </Link>
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label htmlFor="talk_id" className="form-label">
                    Select Talk <span className="text-danger">*</span>
                  </label>
                  <select
                    className={`form-select ${validationErrors.talk_id ? 'is-invalid' : ''}`}
                    id="talk_id"
                    name="talk_id"
                    value={formData.talk_id}
                    onChange={handleChange}
                    disabled={loading || talks.length === 0}
                  >
                    <option value="">Choose a talk...</option>
                    {talks.map(talk => (
                      <option key={talk.talk_id} value={talk.talk_id}>
                        {talk.title}
                      </option>
                    ))}
                  </select>
                  {validationErrors.talk_id && (
                    <div className="invalid-feedback">{validationErrors.talk_id}</div>
                  )}
                  <div className="form-text">
                    Select which talk you want to present in this session
                  </div>
                </div>

                <div className="mb-3">
                  <label htmlFor="event_id" className="form-label">
                    Select Event <span className="text-danger">*</span>
                  </label>
                  <select
                    className={`form-select ${validationErrors.event_id ? 'is-invalid' : ''}`}
                    id="event_id"
                    name="event_id"
                    value={formData.event_id}
                    onChange={handleChange}
                    disabled={loading || showNewEventForm}
                  >
                    <option value="">Choose an event...</option>
                    {events.map(event => (
                      <option key={event.event_id} value={event.event_id}>
                        {event.name} - {new Date(event.start_date).toLocaleDateString()}
                      </option>
                    ))}
                  </select>
                  {validationErrors.event_id && (
                    <div className="invalid-feedback">{validationErrors.event_id}</div>
                  )}
                  <div className="form-text">
                    Select a public event to propose your session
                  </div>
                </div>

                <div className="mb-3">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="newEvent"
                      checked={showNewEventForm}
                      onChange={(e) => {
                        setShowNewEventForm(e.target.checked);
                        if (e.target.checked) {
                          setFormData(prev => ({ ...prev, event_id: '' }));
                        }
                      }}
                      disabled={loading}
                    />
                    <label className="form-check-label" htmlFor="newEvent">
                      Create a new event for this session
                    </label>
                  </div>
                </div>

                {showNewEventForm && (
                  <div className="alert alert-info">
                    <i className="fas fa-info-circle me-2"></i>
                    New event creation will be integrated in the next phase. For now, please select an existing event.
                  </div>
                )}

                <hr className="my-4" />

                <h5 className="mb-3">Scheduling Details (Optional)</h5>

                <div className="mb-3">
                  <label htmlFor="start_time" className="form-label">
                    Start Time
                  </label>
                  <input
                    type="datetime-local"
                    className="form-control"
                    id="start_time"
                    name="start_time"
                    value={formData.start_time}
                    onChange={handleChange}
                    disabled={loading}
                  />
                  <div className="form-text">
                    Preferred start time for your session
                  </div>
                </div>

                <div className="mb-3">
                  <label htmlFor="room_or_track" className="form-label">
                    Room/Track
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="room_or_track"
                    name="room_or_track"
                    value={formData.room_or_track}
                    onChange={handleChange}
                    placeholder="e.g., Main Hall, Track A"
                    disabled={loading}
                  />
                  <div className="form-text">
                    Preferred room or track for your session
                  </div>
                </div>

                <div className="alert alert-info">
                  <i className="fas fa-info-circle me-2"></i>
                  <strong>Session Status:</strong>
                  {formData.event_id ? (
                    <span> Your session will be submitted as <strong>draft</strong> and require approval from the event organizer.</span>
                  ) : showNewEventForm ? (
                    <span> Sessions for new events are automatically <strong>confirmed</strong>.</span>
                  ) : (
                    <span> Select an event to continue.</span>
                  )}
                </div>

                <div className="d-flex gap-2">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading || talks.length === 0}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Creating...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-paper-plane me-2"></i>
                        Submit Session Proposal
                      </>
                    )}
                  </button>
                  <Link to="/sessions" className="btn btn-secondary">
                    Cancel
                  </Link>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionFormPage;
